import React, { useMemo } from 'react';
import { Form as FinalForm } from 'react-final-form';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import * as validators from '../../../util/validators';
import appSettings from '../../../config/settings';
import { formatMoney } from '../../../util/currency';
import { types as sdkTypes } from '../../../util/sdkLoader';

import {
  Modal,
  Button,
  FieldCurrencyInput,
  FieldTextInput,
  Form,
} from '../../../components';

import css from './UpdateBidModal.module.css';

const { Money } = sdkTypes;

/**
 * UpdateBidModal component allows users to update their existing bid on a listing.
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Callback to close the modal
 * @param {Function} props.onManageDisableScrolling - Callback to manage scroll behavior
 * @param {Function} props.onSubmitBidUpdate - Callback to handle bid update submission
 * @param {boolean} props.updateBidInProgress - Whether bid update is in progress
 * @param {Error|null} props.updateBidError - Error from bid update attempt
 * @param {string} props.marketplaceCurrency - Currency code for the marketplace
 * @param {string} props.listingTitle - Title of the listing
 * @param {Money} props.currentBidAmount - Current bid amount as Money object
 * @param {number} props.highestBidAmount - Highest bid amount in subunits (excluding user's bid)
 * @param {number} props.minBidAmountInSubunits - Minimum allowed bid in subunits
 * @param {string} props.transactionId - Transaction ID of the bid to update
 * @returns {React.Component} UpdateBidModal component
 */
const UpdateBidModal = props => {
  const {
    isOpen,
    onClose,
    onManageDisableScrolling,
    onSubmitBidUpdate,
    updateBidInProgress,
    updateBidError,
    marketplaceCurrency,
    listingTitle,
    currentBidAmount,
    highestBidAmount,
    minBidAmountInSubunits,
    transactionId,
  } = props;

  const intl = useIntl();

  // Memoize validation logic to prevent re-creation on every render
  const bidValidators = useMemo(() => {
    const priceRequired = validators.required(
      intl.formatMessage({ id: 'UpdateBidModal.bidAmountRequired' })
    );
    
    // The minimum bid must be higher than the current highest bid
    // Add the currency increment to ensure it's at least +1 unit higher
    const minBidAmount = typeof minBidAmountInSubunits === 'number' ? minBidAmountInSubunits : 1;
    
    // Get currency divisor for the increment
    const unitDivisor = appSettings.getCurrencyFormatting(marketplaceCurrency)?.unitDivisor || 100;
    const minRequiredBid = minBidAmount > 0 ? minBidAmount + unitDivisor : unitDivisor;
    
    // Show message with the actual highest bid amount (not the +1 increment)
    const minBidMessage =
      minBidAmount > 0
        ? intl.formatMessage(
            { id: 'UpdateBidModal.bidAmountBelowMinimum' },
            { minBid: formatMoney(intl, new Money(minBidAmount, marketplaceCurrency)) }
          )
        : intl.formatMessage({ id: 'UpdateBidModal.bidAmountTooLow' });
    
    const minBid = validators.moneySubUnitAmountAtLeast(minBidMessage, minRequiredBid);
    return validators.composeValidators(priceRequired, minBid);
  }, [intl, minBidAmountInSubunits, marketplaceCurrency]);

  // Pre-fill the current bid amount (ensure it is a Money object)
  const initialValues = useMemo(() => {
    const currentBidMoney =
      currentBidAmount instanceof Money
        ? currentBidAmount
        : typeof currentBidAmount === 'number'
        ? new Money(currentBidAmount, marketplaceCurrency)
        : null;

    return {
      bidAmount: currentBidMoney,
    };
  }, [currentBidAmount, marketplaceCurrency]);

  return (
    <Modal
      id="UpdateBidModal"
      isOpen={isOpen}
      onClose={onClose}
      usePortal
      onManageDisableScrolling={onManageDisableScrolling}
      contentClassName={css.modalContent}
    >
      <div className={css.header}>
        <h2 className={css.title}>
          <FormattedMessage id="UpdateBidModal.title" />
        </h2>
        <p className={css.subtitle}>
          <FormattedMessage id="UpdateBidModal.subtitle" values={{ listingTitle }} />
        </p>
        
        <div className={css.bidInfo}>
          <div className={css.bidRow}>
            <span className={css.bidLabel}>
              <FormattedMessage id="UpdateBidModal.highestBidLabel" />:
            </span>
            <span className={css.bidAmount}>
              {highestBidAmount > 0 
                ? formatMoney(intl, new Money(highestBidAmount, marketplaceCurrency))
                : formatMoney(intl, new Money(0, marketplaceCurrency))
              }
            </span>
          </div>
          
          <div className={css.bidRow}>
            <span className={css.bidLabel}>
              <FormattedMessage id="UpdateBidModal.yourCurrentBidLabel" />:
            </span>
            <span className={css.bidAmount}>
              {currentBidAmount instanceof Money
                ? formatMoney(intl, currentBidAmount)
                : typeof currentBidAmount === 'number'
                ? formatMoney(intl, new Money(currentBidAmount, marketplaceCurrency))
                : formatMoney(intl, new Money(0, marketplaceCurrency))
              }
            </span>
          </div>
        </div>
      </div>

      <FinalForm
        initialValues={initialValues}
        onSubmit={values => {
          const bidAmountInSubunits = values.bidAmount?.amount;
          const currency = values.bidAmount?.currency || marketplaceCurrency;
          onSubmitBidUpdate({ 
            transactionId,
            bidAmountInSubunits, 
            currency, 
            message: values.message || '' 
          });
        }}
        render={({ handleSubmit, invalid }) => (
          <Form onSubmit={handleSubmit} className={css.form}>
            <FieldCurrencyInput
              id="updateBidAmount"
              name="bidAmount"
              className={css.field}
              label={intl.formatMessage({ id: 'UpdateBidModal.newBidAmountLabel' })}
              placeholder={intl.formatMessage({ id: 'UpdateBidModal.newBidAmountPlaceholder' })}
              currencyConfig={appSettings.getCurrencyFormatting(marketplaceCurrency)}
              validate={bidValidators}
            />

            <FieldTextInput
              id="updateBidMessage"
              name="message"
              type="textarea"
              className={css.field}
              label={intl.formatMessage({ id: 'UpdateBidModal.messageLabel' })}
              placeholder={intl.formatMessage({ id: 'UpdateBidModal.messagePlaceholder' })}
            />

            {updateBidError ? (
              <p className={css.error}>
                {updateBidError?.message ? updateBidError.message : <FormattedMessage id="UpdateBidModal.updateBidError" />}
              </p>
            ) : null}

            <div className={css.actions}>
              <Button
                type="button"
                onClick={onClose}
                className={css.cancelButton}
              >
                <FormattedMessage id="UpdateBidModal.cancel" />
              </Button>
              <Button
                type="submit"
                inProgress={updateBidInProgress}
                disabled={invalid || updateBidInProgress}
                className={css.submitButton}
              >
                <FormattedMessage id="UpdateBidModal.submit" />
              </Button>
            </div>
          </Form>
        )}
      />
    </Modal>
  );
};

export default UpdateBidModal;
